import * as React from 'react';
import { TooltipContainer, TooltipContent, TooltipTrigger } from './ui/tooltip';

export interface TooltipProps {
  /**
   * The content to display in the tooltip
   */
  content: React.ReactNode;

  /**
   * The element that will trigger the tooltip
   */
  children: React.ReactNode;

  /**
   * Delay in milliseconds before the tooltip appears
   * @default 500
   */
  delay?: number;

  /**
   * The preferred side of the trigger to render against
   * @default "top"
   */
  side?: 'top' | 'right' | 'bottom' | 'left';

  /**
   * The distance in pixels from the trigger
   * @default 4
   */
  sideOffset?: number;

  /**
   * Whether the tooltip is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Custom class name for the tooltip content
   */
  className?: string;

  /**
   * Custom class name for the tooltip arrow
   */
  arrowClassName?: string;

  /**
   * Whether to disable default styling (background, padding, etc.)
   * @default false
   */
  unstyled?: boolean;

  /**
   * Whether to hide the tooltip arrow
   * @default false
   */
  hideArrow?: boolean;

  /**
   * Whether the tooltip should stay open when hovering over the content
   * @default true
   */
  disableHoverableContent?: boolean;
}

/**
 * A tooltip component that displays helpful information when hovering over an element.
 *
 * @example
 * ```tsx
 * <Tooltip content="This is a helpful tooltip">
 *   <button>Hover me</button>
 * </Tooltip>
 * ```
 */
export function Tooltip({
  content,
  children,
  delay = 500,
  side = 'top',
  sideOffset = 4,
  disabled = false,
  className,
  arrowClassName,
  unstyled = false,
  hideArrow = false,
  disableHoverableContent = true,
}: TooltipProps) {
  // Don't render tooltip wrapper if disabled
  if (disabled || !content) {
    return children;
  }

  const isString = typeof content === 'string';

  return (
    <TooltipContainer delayDuration={delay} disableHoverableContent={disableHoverableContent}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent
        side={side}
        sideOffset={sideOffset}
        className={className}
        arrowClassName={arrowClassName}
        unstyled={unstyled}
        hideArrow={hideArrow}
      >
        {isString ? <div dangerouslySetInnerHTML={{ __html: content as string }} /> : content}
      </TooltipContent>
    </TooltipContainer>
  );
}
